import { isUserData } from "common/Database";
import { LoadingIndicator } from "designer/apps/orgs/orgs-styles";
import * as React from "react";
import { useSelector } from "react-redux";
import { RootState } from "store/types";
import InputLabel from "designer/ui/smart/input-label/input-label";
import { GlideDropdown } from "designer/ui/smart/glide-dropdown/glide-dropdown";
import { OrgIcon } from "designer/apps/orgs/orgs-sidebar/org-icon";
import { companySizes } from "./create-team";
import { Button } from "designer/ui/smart/button/button-style";
import { setOrgOnboarding } from "lib/firebase-functions";
import { trackEvent } from "analytics";
import { OnboardingInfo } from "./onboardingInfo";

interface Props {
    orgID: string;
    updateOnboardingInfo(updates: Partial<OnboardingInfo>): void;
    onDone(): void;
}

const lineOfWorkOptions: Record<string, string | undefined> = {
    Select: undefined,
    Sales: "Sales",
    "Logistics / Distribution": "Logistics / Distribution",
    "Inventory Management": "Inventory Management",
    "HR & Operations": "HR & Operations",
    IT: "IT",
    Management: "Management",
    "Project Management": "Project Management",
    "Real Estate": "Real Estate",
    Finance: "Finance",
    Education: "Education",
    Other: "Other",
};

const CompanyFollowUpQuestions: React.FunctionComponent<Props> = ({ orgID, onDone, updateOnboardingInfo }) => {
    const userData = useSelector((state: RootState) => state.userData);
    const orgs = useSelector((state: RootState) => state.organizations);
    const org = orgs.find(o => o.id === orgID);
    const [teamName, setTeamName] = React.useState(org?.displayName || "");
    const [companySize, setCompanySize] = React.useState("Select");
    const [lineOfWork, setLineOfWork] = React.useState("Select");
    const [submitting, setSubmtting] = React.useState(false);

    const teamNameTrimmed = teamName.trim();

    const valid: boolean = lineOfWork !== "Select" && companySize !== "Select" && teamNameTrimmed !== "" && !submitting;

    const onSubmit = React.useCallback(async () => {
        const size = companySizes[companySize];
        const industry = lineOfWorkOptions[lineOfWork];
        if (valid && size !== undefined && industry !== undefined) {
            setSubmtting(true);
            trackEvent("sign up company survey", {
                name: teamNameTrimmed,
                industry,
                role: lineOfWork,
            });
            const result = await setOrgOnboarding({
                organizationID: orgID,
                displayName: teamNameTrimmed,
                companyInformation: {
                    industryKind: industry,
                    numEmployees: size,
                    numAppEditors: { min: 1, max: 1 },
                    numAppUsers: { min: 1, max: 1 },
                },
            });

            updateOnboardingInfo({
                company: teamNameTrimmed,
                lineOfWork: industry,
                companySize: `${size.min}-${size.max}`,
            });

            setSubmtting(false);
            if (result?.ok) {
                onDone();
            }
        }
    }, [companySize, lineOfWork, valid, teamNameTrimmed, orgID, updateOnboardingInfo, onDone]);

    if (!isUserData(userData)) {
        return <LoadingIndicator />;
    }

    return (
        <>
            <hgroup>
                <OrgIcon size={"xlarge"} label={teamNameTrimmed} />
                <h1>Tell us more</h1>
            </hgroup>

            <div className="field">
                <label>Company Name</label>
                <InputLabel
                    margin={8}
                    autoFocus={true}
                    focusBorder={true}
                    label={undefined}
                    placeholder={"Enter your company name"}
                    debounceUpdate={true}
                    value={teamName}
                    onChange={e => setTeamName(e.target.value)}
                    tallerVariation={true}
                />
            </div>

            <div className="field">
                <label>Company Size</label>
                <GlideDropdown
                    items={Object.keys(companySizes)}
                    descriptionForItem={i => i}
                    selected={companySize}
                    onItemSelect={setCompanySize}
                    tallerVariation={true}
                />
            </div>

            <div className="field">
                <label>What is your line of work?</label>
                <GlideDropdown
                    items={Object.keys(lineOfWorkOptions)}
                    descriptionForItem={i => i}
                    selected={lineOfWork}
                    onItemSelect={setLineOfWork}
                    tallerVariation={true}
                />
            </div>
            <div className="continue">
                <Button variant="filled" disabled={!valid} onClick={onSubmit} tallerVariation={true}>
                    {submitting ? `Updating your information...` : `Continue`}
                </Button>
            </div>
        </>
    );
};

export default CompanyFollowUpQuestions;
